import { Box, Typography, useTheme } from "@mui/material";
import { CHART_PADDING_TOP_COEFICIENT } from "Charts/Utils/CommonChartUtils";
import { generateFinancialReserveChartData, getFinancialReserveChartAnnotations } from "Charts/Utils/FinancialReserveChartUtils";
import { CurrencyCode } from "Infrastructure/Models/CurrencyCodes";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { formatCurrency } from "Infrastructure/Utils/CurrencyUtils";
import { getYearsInflected } from "Infrastructure/Utils/LanguageUtils";
import { Chart } from "chart.js";
import AnnotationPlugin from "chartjs-plugin-annotation";
import { addMonths, differenceInMonths, format, monthsToYears } from "date-fns";
import type React from "react";
import { Line } from "react-chartjs-2";

type Props = {
  readonly currentAmount: number;
  readonly targetAmount: number;
  readonly targetDate: Date;
  readonly referenceStartDate: Date;
  readonly monthlyContributionAmount: number;
  readonly inflationRate: number;
  readonly interestRate: number;
};

Chart.register(AnnotationPlugin);

const getLabels = (lengthInMonths: number, referenceStartDate: Date) => {
  const padding = Math.floor(lengthInMonths * 0.05);

  return new Array(lengthInMonths + padding).fill("").map((_, index) => {
    return format(addMonths(referenceStartDate, index), "yyyy-MM-dd");
  });
};

export const FinancialReserveChart: React.FC<Props> = ({
  currentAmount,
  interestRate,
  inflationRate,
  monthlyContributionAmount,
  targetDate,
  targetAmount,
  referenceStartDate,
}) => {
  const { t } = useResource();
  const theme = useTheme();
  const rawAccumulationMonths = differenceInMonths(targetDate, referenceStartDate);
  const accumulationPhaseDuration = Math.max(1, rawAccumulationMonths);
  const data = generateFinancialReserveChartData({
    accumulationPhaseDuration,
    currentAmount,
    inflationRate,
    interestRate,
    monthlyContributionAmount,
  });

  const accumulatedValue = data.accumulationPhaseData[data.accumulationPhaseData.length - 1];

  const getDatasets = () => {
    const paddedTop = targetAmount * CHART_PADDING_TOP_COEFICIENT;
    return [
      {
        data: [paddedTop],
        fill: true,
        borderColor: "transparent",
        backgroundColor: "transparent",
        order: 2,
      },
      {
        data: data.accumulationPhaseData,
        fill: true,
        pointStyle: "dash",
        borderColor: "transparent",
        backgroundColor: theme.colors.chartNeutralLight,
        order: 2,
      },
      {
        data: data.appreciationPhaseData,
        fill: true,
        pointStyle: "dash",
        borderColor: "transparent",
        order: 3,
        backgroundColor: theme.colors.chartPositive,
      },
      {
        data: data.currentReserveData,
        fill: true,
        pointStyle: "dash",
        borderColor: "transparent",
        order: 1,
        backgroundColor: theme.colors.chartNeutral,
      },
    ];
  };

  const options = {
    locale: "en-US",
    interaction: {
      mode: "index" as const,
      intersect: false,
    },
    plugins: {
      title: {
        display: false,
      },
      legend: {
        display: false,
      },

      annotation: {
        annotations: {
          ...getFinancialReserveChartAnnotations(
            theme,
            accumulatedValue > targetAmount ? accumulatedValue : targetAmount,
            currentAmount,
            accumulationPhaseDuration,
          ),
        },
      },
      tooltip: {
        enabled: false,
        external: (context: any) => {
          let tooltipEl = document.getElementById("chartjs-tooltip");

          if (!tooltipEl) {
            tooltipEl = document.createElement("div");
            tooltipEl.id = "chartjs-tooltip";
            tooltipEl.innerHTML = "<table></table>";
            document.body.appendChild(tooltipEl);
          }

          const tooltipModel = context.tooltip;
          if (tooltipModel.opacity === 0) {
            tooltipEl.style.opacity = "0";
            return;
          }

          tooltipEl.classList.remove("above", "below", "no-transform");
          if (tooltipModel.yAlign) {
            tooltipEl.classList.add(tooltipModel.yAlign);
          } else {
            tooltipEl.classList.add("no-transform");
          }

          function getBody(bodyItem: any) {
            return bodyItem.lines;
          }

          const isInAccumulationPhase = tooltipModel.dataPoints[0].dataIndex < data.accumulationPhaseData.length;

          if (tooltipModel.body) {
            const bodyLines = tooltipModel.body.map(getBody);
            let innerHtml = "<thead>";

            if (isInAccumulationPhase) {
              const accumulationPhaseYears = monthsToYears(data.accumulationPhaseData.length);
              innerHtml += `</thead><tbody><tr><td style='font-weight: 500;'>
                          ${t(Resources.AnalysisDetail.Goals.EmergencyFundCreation)}: ${accumulationPhaseYears} ${t(getYearsInflected(accumulationPhaseYears))}
                          </td></tr>`;
            } else {
              const drawdownPhaseYears = monthsToYears(data.appreciationPhaseData.length - data.accumulationPhaseData.length);
              innerHtml += `</thead><tbody><tr><td style='font-weight: 500;'>
                          ${t(Resources.AnalysisDetail.Goals.EmergencyFundAppreciation)}: ${drawdownPhaseYears} ${t(getYearsInflected(drawdownPhaseYears))}
                          </td></tr>`;
            }

            const date = format(new Date(tooltipModel.dataPoints[0].label), "M/yyyy");
            const amount = `<span>${formatCurrency(Number(bodyLines[isInAccumulationPhase ? 2 : 0][0].toString().replaceAll(",", "")), CurrencyCode.CZK, 0)}</span>`;

            innerHtml += `<tr><td>${date}</td></tr><tr><td>${amount}</td></tr>`;
            innerHtml += "</tbody>";

            const tableRoot = tooltipEl.querySelector("table");

            if (tableRoot) {
              tableRoot.innerHTML = innerHtml;
            }
          }

          const chartPosition = context.chart.canvas.getBoundingClientRect();
          const tooltipPosition = tooltipEl.getBoundingClientRect();

          const width = tooltipPosition.right - tooltipPosition.left;
          const height = tooltipPosition.bottom - tooltipPosition.top;

          tooltipEl.style.opacity = "1";
          tooltipEl.style.position = "absolute";
          tooltipEl.style.left = `${chartPosition.left + window.pageXOffset + tooltipModel.caretX - width}px`;
          tooltipEl.style.top = `${chartPosition.top + window.pageYOffset + tooltipModel.caretY - height}px`;
          tooltipEl.style.padding = `${tooltipModel.padding}px`;
          tooltipEl.style.pointerEvents = "none";
          tooltipEl.style.zIndex = "10000";

          tooltipEl.style.fontFamily = "Roboto";
          tooltipEl.style.fontSize = "10px";
          tooltipEl.style.lineHeight = "1.1";
          tooltipEl.style.borderWidth = "1px";
          tooltipEl.style.borderStyle = "solid";
          tooltipEl.style.borderColor = theme.colors.borderLight;
          tooltipEl.style.backgroundColor = theme.colors.white;
          tooltipEl.style.borderRadius = theme.spacing(Radius.M);
          tooltipEl.style.padding = theme.spacing(Spacing.M);
        },
      },
    },
    scales: {
      x: {
        ticks: {
          maxRotation: 0,
          autoSkip: false,
          font: {
            color: "black",
            weight: 600,
            size: 10,
            family: "Roboto",
            lineHeight: 1.3,
          },
          callback: (value: string | number, index: number) => {
            const date = referenceStartDate;
            const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);

            if (index === accumulationPhaseDuration) {
              return format(targetDate, "d.M.yyyy");
            }

            // first date
            if (index === 0 || index === data.appreciationPhaseData.length) {
              return format(addMonths(firstDayOfMonth, Number(value)), "yyyy");
            }

            if (index > data.actualDurationInMonths) {
              return "";
            }
          },
        },
        grid: {
          display: false,
        },
      },
      y: {
        suggestedMin: 0,
        beginAtZero: true,
        ticks: {
          display: false,
        },
        grid: {
          display: false,
        },
      },
    },
    layout: {
      padding: { right: 32 },
    },
  };

  return (
    <Box>
      <Typography variant="caption" fontSize={10} fontWeight={400} lineHeight={1.3} color="secondary">
        {t(Resources.Common.Value)}
      </Typography>
      <Box display="flex" alignItems="end">
        <Line
          options={options}
          data={{
            labels: getLabels(data.actualDurationInMonths, referenceStartDate),
            datasets: getDatasets(),
          }}
        />
        <Typography variant="caption" fontSize={10} fontWeight={400} lineHeight={1.3} color="secondary" mb={5.2} ml={-5}>
          {t(Resources.Common.Time)}
        </Typography>
      </Box>
    </Box>
  );
};
